import { useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import JobAccordionCard from '../components/jobs/JobAccordionCard';
import { publicHiringStatus } from '../data/hiringStatus';
import PageHero from '../components/layout/PageHero';
import { getPublishedJobs } from '../services/jobService';

function JobsPage() {
  const [state, setState] = useState({
    loading: true,
    error: '',
    categories: [],
    hiringStatus: publicHiringStatus,
  });
  const [openJobId, setOpenJobId] = useState('');

  useEffect(() => {
    let isMounted = true;

    getPublishedJobs()
      .then((data) => {
        if (isMounted) {
          setState({
            loading: false,
            error: '',
            categories: data.categories || [],
            hiringStatus: data.hiringStatus || publicHiringStatus,
          });
        }
      })
      .catch(() => {
        if (isMounted) {
          setState({
            loading: false,
            error: 'Unable to load current openings right now.',
            categories: [],
            hiringStatus: publicHiringStatus,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const totalOpenings = useMemo(
    () => state.categories.reduce((total, category) => total + (category.openingsCount || 0), 0),
    [state.categories]
  );
  const acceptingApplications = state.hiringStatus.acceptingApplications === true;

  return (
    <>
      <PageHero
        eyebrow="CAREERS"
        title="Current Openings"
        description="Explore current opportunities at FactorStreet. More roles will be added as our projects grow."
      />
      <section className="section jobs-page">
        <div className="container">
          <div className="jobs-flow-cta">
            <div>
              <p className="eyebrow">Before you apply</p>
              <h2>Understand the process and submit the right application.</h2>
            </div>
            <div className="jobs-flow-cta__actions">
              {state.hiringStatus.acceptingApplications !== true ? (
                <span className="hiring-status-badge hiring-status-badge--closed">
                  {state.hiringStatus.closedLabel || publicHiringStatus.closedLabel}
                </span>
              ) : null}
              <Button className="jobs-flow-cta__process-button" to="/hiring-process" variant="secondary">
                <span>Hiring Process</span>
              </Button>
            </div>
          </div>

          {state.error ? <p className="form-alert form-alert--error">{state.error}</p> : null}

          {state.loading ? <p className="jobs-empty">Loading current openings...</p> : null}

          {!state.loading && totalOpenings === 0 ? (
            <div className="jobs-empty-card">
              <p className="eyebrow">No active openings</p>
              <h2>No openings available right now.</h2>
              <p>
                {acceptingApplications
                  ? 'You can still share your profile through Apply Now.'
                  : publicHiringStatus.closedMessage}
              </p>
              {acceptingApplications ? <Button to="/apply">Apply Now</Button> : null}
            </div>
          ) : null}

          <div className="job-category-list">
            {state.categories.map((category) => (
              <section className="job-category-section" key={category.slug}>
                <div className="job-category-section__header">
                  <div>
                    <h2>{category.name}</h2>
                    {category.description ? <p>{category.description}</p> : null}
                  </div>
                  <span>
                    {category.openingsCount} {category.openingsCount === 1 ? 'opening' : 'openings'}
                  </span>
                </div>

                <div className="job-accordion-list">
                  {category.jobs.map((job) => (
                    <JobAccordionCard
                      acceptingApplications={acceptingApplications}
                      isOpen={openJobId === job._id}
                      job={job}
                      key={job._id}
                      onToggle={() => setOpenJobId((current) => (current === job._id ? '' : job._id))}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default JobsPage;
